// Pure per-tick gameplay rules — physics, enemy patrol, collisions, scoring,
// checkpoint/fall/finish. Shared by the multiplayer DO tick loop
// (server/game-session.ts) and the client's local solo simulation
// (client/solo-sim.ts) so the two modes can never drift apart on rules.
//
// Every function takes the active `LevelConfig` explicitly rather than
// reading a module-level constant, so the same rules run unmodified across
// all ten levels (see level.ts).

import { ENEMY_HEIGHT, ENEMY_WIDTH, PLAYER_HEIGHT } from "./level";
import { isStomp, playerRect, rectsOverlap, seagullY, stepPatrol, stepPlayer, STOMP_BOUNCE_VELOCITY } from "./server/physics";

import type { LevelConfig } from "./level";
import type { GameInput } from "./shared";

export const RESPAWN_INVINCIBILITY_MS = 1500;
export const POWER_UP_INVINCIBILITY_MS = 5000;
export const ENEMY_STOMP_POINTS = 20;
export const COLLECTIBLE_PICKUP_SIZE = 20;
export const POWER_UP_PICKUP_SIZE = 24;
export const FALL_DEATH_MARGIN = 100;
export const BASE_TIME_BONUS = 600;
export const TIME_BONUS_PER_SEC = 10;

export type EnemyState = { x: number; y: number; dir: 1 | -1; alive: boolean };

export type PlayerSimState = {
	x: number;
	y: number;
	vx: number;
	vy: number;
	onGround: boolean;
	facing: "left" | "right";
	lives: number;
	score: number;
	checkpointX: number;
	invincibleUntil: number;
	finished: boolean;
	gameOver: boolean;
	startTimeMs: number;
};

export function createInitialPlayer(now: number, level: LevelConfig): PlayerSimState {
	return {
		x: level.spawnX,
		y: level.groundY - PLAYER_HEIGHT,
		vx: 0,
		vy: 0,
		onGround: true,
		facing: "right",
		lives: 3,
		score: 0,
		checkpointX: level.spawnX,
		invincibleUntil: 0,
		finished: false,
		gameOver: false,
		startTimeMs: now,
	};
}

export function createInitialEnemies(level: LevelConfig): Map<string, EnemyState> {
	const enemies = new Map<string, EnemyState>();
	for (const spawn of level.enemies) enemies.set(spawn.id, { x: spawn.x, y: spawn.y, dir: 1, alive: true });
	return enemies;
}

export function applyHit(player: PlayerSimState, now: number, level: LevelConfig): void {
	player.lives -= 1;
	if (player.lives <= 0) {
		player.gameOver = true;
		player.vx = 0;
		player.vy = 0;
		return;
	}
	player.x = player.checkpointX;
	player.y = level.groundY - PLAYER_HEIGHT;
	player.vx = 0;
	player.vy = 0;
	player.onGround = true;
	player.invincibleUntil = now + RESPAWN_INVINCIBILITY_MS;
}

export function stepEnemies(enemies: Map<string, EnemyState>, level: LevelConfig, elapsedSec: number, dtSec: number): void {
	for (const [id, enemy] of enemies) {
		if (!enemy.alive) continue;
		const spawn = level.enemies.find((e) => e.id === id);
		if (!spawn) continue;
		const { x, dir } = stepPatrol(enemy.x, enemy.dir, spawn.patrolMinX, spawn.patrolMaxX, spawn.speed, dtSec);
		enemy.x = x;
		enemy.dir = dir;
		if (spawn.type === "seagull") enemy.y = seagullY(spawn.y, elapsedSec);
	}
}

export type TickResult = { collectedIds: string[]; finishTimeMs?: number };

// Advances one player by one tick: physics, enemy collisions, pickups,
// checkpoint/fall/finish. Mutates `player` and `enemies` (aliveness) and
// `collectiblesRemaining`/`powerUp` in place; returns the events the caller
// needs for broadcasting/bookkeeping (score itself is already applied).
export function stepPlayerTick(
	player: PlayerSimState,
	input: GameInput | undefined,
	enemies: Map<string, EnemyState>,
	collectiblesRemaining: Set<string>,
	powerUp: { taken: boolean },
	now: number,
	dtSec: number,
	level: LevelConfig,
): TickResult {
	const result: TickResult = { collectedIds: [] };
	if (player.finished || player.gameOver) return result;

	const prevBottom = player.y + PLAYER_HEIGHT;
	const next = stepPlayer(
		{ x: player.x, y: player.y, vx: player.vx, vy: player.vy, onGround: player.onGround },
		input,
		level.platforms,
		level.groundY,
		level.width,
		dtSec,
		level.groundGaps,
	);
	player.x = next.x;
	player.y = next.y;
	player.vx = next.vx;
	player.vy = next.vy;
	player.onGround = next.onGround;
	if (next.vx > 0) player.facing = "right";
	else if (next.vx < 0) player.facing = "left";

	const invincible = player.invincibleUntil > now;
	const pRect = playerRect(player.x, player.y);

	for (const [, enemy] of enemies) {
		if (!enemy.alive) continue;
		const eRect = { x: enemy.x, y: enemy.y, width: ENEMY_WIDTH, height: ENEMY_HEIGHT };
		if (!rectsOverlap(pRect, eRect)) continue;
		if (invincible) {
			enemy.alive = false;
			player.score += ENEMY_STOMP_POINTS;
		} else if (isStomp(prevBottom, player.vy, enemy.y)) {
			enemy.alive = false;
			player.score += ENEMY_STOMP_POINTS;
			player.vy = STOMP_BOUNCE_VELOCITY;
		} else {
			applyHit(player, now, level);
		}
		break;
	}

	if (player.gameOver) return result;

	for (const collectible of level.collectibles) {
		if (!collectiblesRemaining.has(collectible.id)) continue;
		const cRect = {
			x: collectible.x,
			y: collectible.y,
			width: COLLECTIBLE_PICKUP_SIZE,
			height: COLLECTIBLE_PICKUP_SIZE,
		};
		if (rectsOverlap(playerRect(player.x, player.y), cRect)) {
			collectiblesRemaining.delete(collectible.id);
			player.score += collectible.value;
			result.collectedIds.push(collectible.id);
		}
	}

	if (!powerUp.taken) {
		const puRect = {
			x: level.powerUp.x,
			y: level.powerUp.y,
			width: POWER_UP_PICKUP_SIZE,
			height: POWER_UP_PICKUP_SIZE,
		};
		if (rectsOverlap(playerRect(player.x, player.y), puRect)) {
			powerUp.taken = true;
			player.invincibleUntil = now + POWER_UP_INVINCIBILITY_MS;
		}
	}

	if (player.y > level.height + FALL_DEATH_MARGIN) applyHit(player, now, level);

	if (player.x >= level.checkpointX) player.checkpointX = level.checkpointX;

	if (!player.gameOver && player.x >= level.finishX) {
		player.finished = true;
		const runSec = (now - player.startTimeMs) / 1000;
		const timeBonus = Math.max(0, BASE_TIME_BONUS - Math.floor(runSec) * TIME_BONUS_PER_SEC);
		player.score += timeBonus;
		result.finishTimeMs = now - player.startTimeMs;
	}

	return result;
}
